import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { FormArray } from '@angular/forms';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfirmationComplaintsP58Model,
    EnvironmentInstanceToken,
    EventService,
    PaceEnvironment,
    UserMasterService,
    BROWSER_CHECKS,
    BROWSER_TYPES,
    ConfirmationComplaintsP58Service,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { IClipboardResponse } from 'ngx-clipboard';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Component({
    selector: 'pace-confirmation-complaints-p58',
    templateUrl: './confirmation-complaints-p58.component.html',
    styleUrls: ['./confirmation-complaints-p58.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationComplaintsP58Component implements OnInit {
    @Input()
    public model: ConfirmationComplaintsP58Model;

    @Input()
    public parentForm: FormArray;

    public isPaceOnline: boolean;

    public isPacePWA: boolean;

    public strings: {
        complaintsHeader: string;
        questionnaireLink: string;
    };

    public isCopied = false;
    public clipboardCopyButtonLabel: string;
    public faClock = faClock;
    public constructor(
        private readonly translate: TranslateService,
        private readonly eventService: EventService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly userMasterService: UserMasterService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        private readonly confirmationComplaintsP58Service: ConfirmationComplaintsP58Service,
    ) {
        this.clipboardCopyButtonLabel = <string>(
            this.translate.instant('CONFIRMATION_COMPLAINTS_ADDITIONAL_QUESTIONNAIRE_LINK_BEFORE_COPY_TEXT')
        );
    }

    public ngOnInit(): void {
        this.strings = {
            complaintsHeader: <string>this.translate.instant('CONFIRMATION_COMPLAINTS_LABEL'),
            questionnaireLink: '',
        };

        this.isPaceOnline = this.userMasterService.isPaceOnlineUser();
        this.isPacePWA = this.clientManager.isPacePWA();
        this.updateQuestionnaireLink();
    }

    public optionChange(): void {
        this.eventService.triggerComplaintOptionSelectionChanged(this.model.showQuestionnaireLink());
        this.updateQuestionnaireLink();
        this.parentForm.controls.forEach((control) => control.updateValueAndValidity());
        this.changeDetectorRef.detectChanges();
    }

    public updateQuestionnaireLink(): void {
        if (!this.model.showQuestionnaireLink()) {
            this.strings.questionnaireLink = '';
            return;
        }
        let ticketType: ConfigConstants.USER_STATUS | string = '';
        const OPTION_YES = ConfigConstants.optionYes;
        if (
            this.model.complaintsQuestion1OptionGroup.selectedEntry.value === OPTION_YES ||
            this.model.complaintsQuestion2OptionGroup.selectedEntry.value === OPTION_YES ||
            this.model.complaintsQuestion3OptionGroup.selectedEntry.value === OPTION_YES
        ) {
            ticketType = ConfigConstants.USER_STATUS.PSI;
        } else if (this.model.complaintsQuestion4OptionGroup.selectedEntry.value === OPTION_YES) {
            ticketType = ConfigConstants.USER_STATUS.PPI;
        }
        if (this.model.complaintsQuestion5OptionGroup?.selectedEntry.value === OPTION_YES) {
            ticketType = ticketType.length
                ? `${ticketType}_${ConfigConstants.USER_STATUS.CYS}`
                : ConfigConstants.USER_STATUS.CYS;
        }
        this.strings.questionnaireLink = `${this.model.complaintsQuestionnaireLink}&QMNUM=${this.model.notificationId.qmNum}&TICKET_TYPE=${ticketType}`;
    }

    public async callAzure(complaintsLinkAzureUrl: string): Promise<void> {
        const payload = {
            link: this.strings.questionnaireLink,
        };
        this.confirmationComplaintsP58Service.saveComplaintParams(payload).subscribe({
            next: (res: { id: string; message: string }) => {
                window.location.href = `microsoft-edge:${complaintsLinkAzureUrl}/${res.id}`;
            },
            error: (err) => {
                this.log.error('Error in POST request:', err);
            },
        });
    }

    public openInEdge(): void {
        const isEdge = BROWSER_CHECKS[BROWSER_TYPES.EDGE]();
        if (isEdge) {
            window.location.href = `${this.strings.questionnaireLink}`;
            return;
        } else {
            const complaintsLinkAzureUrl = this.environment.complaintsLinkUrl;

            if (complaintsLinkAzureUrl) {
                this.callAzure(complaintsLinkAzureUrl).catch((err) => {
                    this.log.error('Error in calling Azure', err);
                });
            } else {
                this.log.error('complaintsLinkUrl is undefined');
            }
        }
    }

    public onClipboardCopySuccess(event: IClipboardResponse): void {
        if (event.isSuccess) {
            this.isCopied = true;
            this.clipboardCopyButtonLabel = <string>(
                this.translate.instant('CONFIRMATION_COMPLAINTS_ADDITIONAL_QUESTIONNAIRE_LINK_SUCCESS_COPY_TEXT')
            );
        }
    }
}
